import { graphQLClient } from '@api/client';
import {
  PaymentMethod,
  Treatment,
  useCreateTreatmentPaymentMutation,
  useTreatmentHistoryDetailQuery
} from '@api/GoodocAPI';
import { useTreatmentInformationContext } from '@context/TreatmentInformation';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  POST_PAYMENT_METHOD_FIRST,
  POST_PAYMENT_METHOD_NEW
} from '@pages/PostPayment/focusKey';
import {
  generateHistoryDetailPath,
  generateTreatmentPaymentCompletePath,
  PAYMENT,
  TargetPayment
} from '@router/path';
import { getTreatmentPrice } from '@utils/treatmentUtil';
import constate from 'constate';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePaymentMethodsContext } from '../PaymentMethods';

const usePostPayment = () => {
  const {paymentMethods} = usePaymentMethodsContext();
  const { selectedCreditCard, setSelectedCreditCard } = useTreatmentInformationContext();

  const { type = TargetPayment.treatment, typeId = '' } = useParams();
  const { data: treatmentHistoryDetailResponse, refetch: refetchTreatmentHistoryDetail } =
    useTreatmentHistoryDetailQuery(
      graphQLClient,
      { treatmentId: typeId },
      { enabled: type === TargetPayment.treatment }
    );

  const {
    isLoading: isLoadingCreateTreatmentPayment,
    mutateAsync: createTreatmentPayment
  } = useCreateTreatmentPaymentMutation(graphQLClient);

  const postPaymentFee = useMemo(() => {
    return getTreatmentPrice(treatmentHistoryDetailResponse?.treatmentV3 as Treatment);
  }, [treatmentHistoryDetailResponse?.treatmentV3]);

  const isPrescriptionExists = useMemo(() => {
    return !!treatmentHistoryDetailResponse?.treatmentV3.prescription?.id;
  }, [treatmentHistoryDetailResponse?.treatmentV3]);

  const tryPostPayment = useCallback(async () => {
    try {
      if (!selectedCreditCard || !selectedCreditCard.id) return;
      if (!treatmentHistoryDetailResponse?.treatmentV3.fee) return;

      if (type === TargetPayment.treatment) {
        await createTreatmentPayment(
          {
            treatmentId: typeId,
            paymentMethodId: selectedCreditCard.id,
            treatmentFee: treatmentHistoryDetailResponse?.treatmentV3.fee
          },
          {
            onSuccess: async () => {
              await refetchTreatmentHistoryDetail();
              navigate(
                generateTreatmentPaymentCompletePath(
                  typeId,
                  !!treatmentHistoryDetailResponse.treatmentV3.prescription
                )
              );
            },
            onError: () => {
              navigate(generateHistoryDetailPath(typeId));
            }
          }
        );
      }
    } catch (e) {
      await refetchTreatmentHistoryDetail();
    }
  }, [type, typeId, treatmentHistoryDetailResponse, selectedCreditCard]);

  const loaderVisible = useMemo(() => {
    return isLoadingCreateTreatmentPayment;
  }, [isLoadingCreateTreatmentPayment]);

  const openTryPostPaymentModal = async (paymentMethod: Partial<PaymentMethod>) => {
    setSelectedCreditCard(paymentMethod);

    await tryPostPayment();
  };

  const { setFocus } = useFocusable();
  const setButtonFocus = useCallback(() => {
    if (paymentMethods.length > 0) {
      setFocus(POST_PAYMENT_METHOD_FIRST);
    } else {
      setFocus(POST_PAYMENT_METHOD_NEW);
    }
  }, [paymentMethods]);

  const navigate = useNavigate();
  const goPaymentRegistration = () => {
    navigate(PAYMENT.REGISTRATION);
  };

  return {
    paymentMethods,
    postPaymentFee,
    isPrescriptionExists,
    setButtonFocus,
    loaderVisible,
    openTryPostPaymentModal,
    goPaymentRegistration
  };
};

export const [PostPaymentProvider, usePostPaymentContext] = constate(usePostPayment);
